import { isNonNull, NonNullableObject } from '@fullstory/typeutils';
import { getAdjustedEndDate, isMidnight } from '@fullstory/util/date';
import moment, { Moment } from 'moment';
import { DateRange, MinMaxMoment, MomentRange } from './types';

export const isValidRange = (range?: MomentRange): range is NonNullableObject<MomentRange> =>
  isNonNull(range?.start) && isNonNull(range?.end);

export const isWithinRange = (day: Moment, range?: MomentRange) => {
  if (range?.start && range.end === null) return isSameDay(day, range.start);
  if (range?.end && range.start === null) return isSameDay(day, range.end);

  return (
    isValidRange(range) &&
    day.isBetween(
      moment(range.start).startOf('day'),
      moment(range.end).endOf('day'),
      undefined,
      '[]',
    )
  );
};

export const isSameDay = (day: Moment, rangeDay?: Moment | null) =>
  isNonNull(rangeDay) && day.isSame(rangeDay, 'day');

const validateStartDate = (date: Moment, minMaxMoment?: MinMaxMoment) => {
  if (!minMaxMoment) return date;
  // if is before min date, return min date
  if (minMaxMoment.min && date.isBefore(minMaxMoment.min)) return moment(minMaxMoment.min);
  // if is after max date, return max date
  if (minMaxMoment.max && date.isAfter(minMaxMoment.max, 'day')) return moment(minMaxMoment.max);
  return date;
};

export const updateRangeStart = (
  range: MomentRange,
  date: Moment,
  minMaxMoment?: MinMaxMoment,
  ongoing?: boolean,
) => {
  const validatedDate = validateStartDate(date, minMaxMoment);
  const { end } = range;
  // This handles the case where a user chooses the same start and end date
  // because they want to add a time or query the entire day as a range.
  // In that case, we should default to end of day instead of start of day.
  if (end && validatedDate.isSame(end, 'day') && validatedDate.isAfter(end) && !ongoing) {
    return { start: validatedDate, end: end.endOf('day') };
  }
  return end && validatedDate.isAfter(end) && !ongoing
    ? { start: validatedDate, end: null }
    : { start: validatedDate, end };
};

export const updateRangeEnd = (range: MomentRange, date: Moment, minMaxMoment?: MinMaxMoment) => {
  let validatedDate = date;
  const { start } = range;
  if (minMaxMoment?.max && date.isAfter(moment(minMaxMoment.max).endOf('day'))) {
    // This handles the case where a user chooses the same start and end date
    // because they want to add a time. In that case, we should default to end of day
    // instead of start of day.
    if (start && date.isSame(start, 'day')) {
      validatedDate = moment(minMaxMoment.max).endOf('day');
    } else {
      validatedDate = moment(minMaxMoment.max);
    }
    // we know the end date is after the max date, so no need to swap
    return { start, end: validatedDate };
  }
  return start && validatedDate.isBefore(start)
    ? { start: validateStartDate(date, minMaxMoment), end: start }
    : { start, end: validatedDate };
};

export const dateRangeToMomentRange = (
  range?: DateRange,
  utc?: boolean,
  preserveRangeEnd?: boolean,
): MomentRange => {
  const makeMoment = utc ? moment.utc : moment;
  /**
   * If the end date is at midnight, it actually represents the previous day due to the way that ranges
   * work in solr. Solr interprets the date and time, so if we want the end date to be 8/7 then we set the
   * end date to 8/8 at 12am so that the entire day of 8/7 is included.
   *
   * We handle this in the DateRangePicker component by internally shifting the day back to 8/7 so that the
   * calendar and date inputs reflect the intended date.
   */

  const getEnd = (end: Date) => (preserveRangeEnd ? makeMoment(end) : getAdjustedEndDate(end, utc));
  return {
    start: range?.start ? makeMoment(range.start) : null,
    end: range?.end ? getEnd(range.end) : null,
  };
};

export const momentRangeToDateRange = (
  range: MomentRange,
  preserveRangeEnd: boolean,
): DateRange => {
  /**
   * When we convert the internal moment range back to a date range to call our change handler with, we
   * must again handle the special end date case. If the user sets the end date without specifying a time,
   * we shift the date forward a day so that the solr range is inclusive of the end date selected.
   *
   * e.g. if 8/7 is selected in the DateRangePicker component, we shift the end date to 8/8 at 12am so
   * that the entire day of 8/7 is included.
   */
  const rangeEnd =
    range?.end && isMidnight(range.end) && !preserveRangeEnd
      ? moment(range.end).add(1, 'day')
      : range?.end;
  return {
    start: range?.start ? range.start.toDate() : null,
    end: rangeEnd ? rangeEnd.toDate() : null,
  };
};
